import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';

import {
  useCognito,
  passwordValidationRegex,
} from '@sandtech-hub-monorepo/ui-auth-kit';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  PasswordInput,
} from '@sandtech-hub-monorepo/ui-kit';

import {
  confirmNewPasswordLabel,
  confirmPasswordRequiredValidationMessage,
  newPasswordLabel,
  newPasswordPlaceholder,
  passwordResetMismatch,
  passwordRulesValidationMessage,
  // Not part of ui-auth-kit's public exports (libs/ui-auth-kit/src/index.ts),
  // same deep import apps/central-hub's own PasswordChange.tsx already uses.
} from '../../../../../libs/ui-auth-kit/src/stringsManager';

const formSchema = z
  .object({
    currentPassword: z.string().min(1, 'Please enter your old password'),
    newPassword: z
      .string()
      .regex(
        new RegExp(passwordValidationRegex),
        passwordRulesValidationMessage
      ),
    confirmNewPassword: z
      .string()
      .min(1, confirmPasswordRequiredValidationMessage),
  })
  .refine(
    ({ newPassword, confirmNewPassword }) => newPassword === confirmNewPassword,
    {
      message: passwordResetMismatch,
      path: ['confirmNewPassword'],
    }
  );

/**
 * Change-password form for a logged-in learner, ported from central-hub's
 * `PasswordChange` (apps/central-hub/src/pages/Settings/components) — same
 * Cognito `changePassword` call, same validation, no set-edu-specific logic.
 * Only shown to GSA learners (see `SettingsPage`'s `showPassword`), matching
 * how the Subscription tab is scoped: FIT degree students don't manage their
 * own Cognito password here.
 */
export const PasswordTab = () => {
  const { changePassword, loading } = useCognito();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const onSubmit = async ({
    currentPassword,
    newPassword,
  }: z.infer<typeof formSchema>) => {
    await changePassword({ oldPassword: currentPassword, newPassword }, () =>
      toast.success('Password successfully changed!')
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-muted-foreground">
        Enter your current password to set a new one.
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex w-full flex-col gap-5 pb-5 sm:w-3/4 md:w-1/2 lg:w-1/3 xl:w-1/4">
            <FormField
              disabled={loading}
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    Current password
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter your current password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              disabled={loading}
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    {newPasswordLabel}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder={newPasswordPlaceholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              disabled={loading}
              control={form.control}
              name="confirmNewPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">
                    {confirmNewPasswordLabel}
                  </FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Confirm new password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" disabled={form.formState.isLoading}>
            {loading ? (
              <Loader2Icon className="animate-spin" />
            ) : (
              'Update password'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};
